import React from "react";
import { css } from "@emotion/react";
import { ExpandedRichText } from "@swo/shared";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { SORichTextRenderer } from "../../components/_shared-components/so-rich-text";
import { CSS_MQ } from "../../styles/breakpoints";
import { FONTS, fontSize } from "../../styles/fonts";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ProductDetailPageDeliveryPackage } from "./PDP_DeliveryPackage/ProductDetailPageDeliveryPackage";
import { DescriptionRichText } from "./PDP_Description/DescriptionRichText";
import { PDPHeadlineTypography } from "./pdp-headline-typography";
import { usePdpTranslations } from "./PdpTranslationsContext";
import type { Translated } from "../../domain/translations/translations";
import type { GeneralTranslation } from "@swo/storefront";
import { T } from "../../components/T";

type PDPDeliveryPackageDescriptionProps = {
  deliveryPackageProps: NonEmptyArray<string> | undefined;
  longDescription: ExpandedRichText | undefined;
  gt: Translated<GeneralTranslation>;
};

export const PDPDeliveryPackageDescription: ReactTFC<PDPDeliveryPackageDescriptionProps> = ({
  gt,
  deliveryPackageProps,
  longDescription,
}) => {
  const t = usePdpTranslations();

  const headline = gt("moreInfoLabel");
  const productDescriptionHeadline = t("productDescriptionHeadline");
  const leftHeadline = t("deliveryPackageHeadline");

  if (!deliveryPackageProps && !longDescription) {
    return null;
  }

  return (
    <>
      <Spacer size={"lg"} />

      <div className={"js-pdp-description"}>
        <ContentContainer>
          <span
            css={[
              PDPHeadlineTypography,
              css`
                display: block;
                margin-bottom: 40px;
              `,
            ]}
          >
            <T>{headline}</T>
          </span>

          <div
            css={css`
              display: grid;

              grid-gap: 40px;
              ${CSS_MQ.atLeastMd} {
                grid-template-columns: minmax(0, 3fr) minmax(0, 5fr);
              }
            `}
          >
            <div>
              {deliveryPackageProps && (
                <ProductDetailPageDeliveryPackage headline={leftHeadline} elements={deliveryPackageProps} />
              )}
            </div>

            {longDescription ? (
              <div>
                <span
                  css={[
                    FONTS.SwarovskiFutura500,
                    fontSize.x25,
                    css`
                      display: block;
                      margin-bottom: 12px;
                    `,
                  ]}
                >
                  <T>{productDescriptionHeadline}</T>
                </span>

                <div css={[FONTS.SwarovskiFutura300, fontSize.x25]}>
                  <DescriptionRichText>
                    <SORichTextRenderer richText={longDescription} options={"with-hyperlinks"} />
                  </DescriptionRichText>
                </div>
              </div>
            ) : null}
          </div>
        </ContentContainer>
      </div>
    </>
  );
};
